import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  Input,
  VStack,
  Flex,
  Text,
  Spinner,
  Checkbox,
  Collapse,
  IconButton,
  Button,
  Menu,
  MenuButton,
  MenuList,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon, AddIcon, CheckIcon } from '@chakra-ui/icons';
import api from '../api';

const InvoiceProductSidebar = ({
  filters,
  setFilters,
  categories,
  selectedCategories,
  setSelectedCategories,
  handleAddProduct,
  selectedProducts,
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState([]); // For tracking expanded categories
  const [selectedProduct, setSelectedProduct] = useState(null); // For detailed product modal
  const placeholderImage = '/placeholder-product.jpeg'; // Placeholder image path

  useEffect(() => {
    fetchProducts(1, selectedCategories);
  }, [filters.name, selectedCategories]);

  const fetchProducts = async (page, categoryIds = []) => {
    setLoading(true);
    try {
      const response = await api.get(
        `/products/?page=${page}&search=${filters.name}&categories=${categoryIds.join(',')}`
      );
      const newProducts = response.data.results;
      setProducts((prev) => (page === 1 ? newProducts : [...prev, ...newProducts]));
      setHasMore(!!response.data.next);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (bottom && hasMore && !loading) {
      fetchProducts(currentPage + 1, selectedCategories);
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handleCategoryChange = (categoryId, category) => {
    const collectChildCategoryIds = (cat) => {
      let ids = [cat.id];
      if (cat.children) {
        cat.children.forEach((child) => {
          ids = ids.concat(collectChildCategoryIds(child));
        });
      }
      return ids;
    };

    const childIds = collectChildCategoryIds(category);

    if (selectedCategories.some((id) => childIds.includes(id))) {
      // Remove all child categories
      setSelectedCategories((prev) => prev.filter((id) => !childIds.includes(id)));
    } else {
      // Add all child categories
      setSelectedCategories((prev) => [...prev, ...childIds]);
    }
  };

  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const isCategorySelected = (category) => {
    const collectChildCategoryIds = (cat) => {
      let ids = [cat.id];
      if (cat.children) {
        cat.children.forEach((child) => {
          ids = ids.concat(collectChildCategoryIds(child));
        });
      }
      return ids;
    };

    const childIds = collectChildCategoryIds(category);
    const selectedChildren = childIds.filter((id) => selectedCategories.includes(id));

    if (selectedChildren.length === childIds.length) {
      return true; // Fully selected
    }
    if (selectedChildren.length > 0) {
      return 'indeterminate'; // Partially selected
    }
    return false; // Not selected
  };

  const renderCategories = (categories, depth = 0) =>
    categories.map((category) => {
      const categoryState = isCategorySelected(category);
      return (
        <Box key={category.id} mb="2" ml={depth * 4}>
          <Flex align="center">
            {category.children?.length > 0 && (
              <IconButton
                icon={
                  expandedCategories.includes(category.id) ? (
                    <ChevronDownIcon />
                  ) : (
                    <ChevronRightIcon />
                  )
                }
                size="sm"
                onClick={() => toggleCategoryExpansion(category.id)}
                variant="ghost"
                aria-label="Toggle Category"
                mr="2"
              />
            )}
            <Checkbox
              isChecked={categoryState === true}
              isIndeterminate={categoryState === 'indeterminate'}
              onChange={() => handleCategoryChange(category.id, category)}
            >
              {category.name}
            </Checkbox>
          </Flex>
          {category.children?.length > 0 && (
            <Collapse in={expandedCategories.includes(category.id)} animateOpacity>
              {renderCategories(category.children, depth + 1)}
            </Collapse>
          )}
        </Box>
      );
    });

  const handleViewProductDetails = (product) => {
    setSelectedProduct(product);
  };

  const handleCloseProductModal = () => {
    setSelectedProduct(null);
  };

  return (
    <Box flex="1" maxW="350px" borderRight="1px solid #e2e8f0" h="100%">
      {/* Search and Category Selector */}
      <Box position="sticky" top="0" bg="white" zIndex="1" borderBottom="1px solid #e2e8f0" p="4">
        {/* Search Filter */}
        <FormControl mb="4">
          <Input
            placeholder="Пошук за назвою"
            value={filters.name}
            onChange={(e) => setFilters({ ...filters, name: e.target.value })}
          />
        </FormControl>

        {/* Category Selector Dropdown */}
        <FormControl mb="4">
          <Menu>
            <MenuButton as={Button} rightIcon={<ChevronDownIcon />} w="100%">
              Обрати категорії
            </MenuButton>
            <MenuList p="4" maxH="300px" overflowY="auto" w="115%">
            {categories.length > 0 
                ? renderCategories(categories) 
                : <Text>Категорії не знайдено</Text>}
            </MenuList>
          </Menu>
        </FormControl>
      </Box>

      {/* Product List */}
      <Box overflowY="auto" maxH="calc(100% - 150px)" p="2" onScroll={handleScroll}>
        {products.length > 0 ? (
          <VStack spacing="1" align="stretch">
            {products.map((product) => (
              <Box
                key={product.id}
                p="4"
                borderWidth="1px"
                borderRadius="md"
                bg="gray.50"
                _hover={{ bg: 'gray.100' }}
                cursor="pointer"
              >
                <Flex align="center" justify="space-between" gap="4">
                  <Image
                    src={product.images?.[0]?.image || placeholderImage}
                    alt={product.name}
                    boxSize="50px"
                    objectFit="cover"
                    borderRadius="md"
                    onClick={() => handleViewProductDetails(product)}
                    cursor="pointer"
                    flexShrink={0}
                  />

                  <Box flex="1" minW="0">
                    <Text fontWeight="bold" isTruncated={false} whiteSpace="normal" wordBreak="break-word">
                      {product.name}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Ціна: {product.price}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Кількість: {product.stock_quantity}
                    </Text>
                  </Box>

                  {selectedProducts.includes(product.id) ? (
                    <IconButton icon={<CheckIcon />} isDisabled colorScheme="green" />
                  ) : (
                    <IconButton
                      icon={<AddIcon />}
                      onClick={() => handleAddProduct(product)}
                      colorScheme="blue"
                    />
                  )}
                </Flex>
              </Box>
            ))}
          </VStack>
        ) : (
          <Text textAlign="center" color="gray.500" mt="4">
            Товарів не знайдено
          </Text>
        )}
        {loading && <Spinner />}
      </Box>

      {/* Product Details Modal */}
      {selectedProduct && (
        <Modal isOpen={!!selectedProduct} onClose={handleCloseProductModal} size="3xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{selectedProduct.name}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Image
                src={selectedProduct.images?.[0]?.image || '/placeholder-product.jpeg'}
                alt={selectedProduct.name}
                mb="4"
                borderRadius="md"
              />
              <Text>{selectedProduct.description || 'Опис недоступний.'}</Text>
              <Text fontWeight="bold" mt="4">
                Ціна: {selectedProduct.price} грн
              </Text>
              <Text fontWeight="bold" mt="4">
                Ціна з ПДВ: {selectedProduct.price_with_vat} грн
              </Text>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};

export default InvoiceProductSidebar;
